import React, { useMemo, useState, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, Square, ArrowRight, Filter, TreePine } from 'lucide-react';
import { Link } from 'react-router-dom';
import { PageHeader } from '../components/PageHeader';
import { lands, Property } from '../data/properties';
const statusFilters = ['All', 'Selling', 'Sold Out'] as const;
function StatusPill({
  status
}: {
  status: Property['status'];
}) {
  const map = {
    Selling: 'bg-emerald-500 text-white',
    'Sold Out': 'bg-ink text-white',
    'Sale Closed': 'bg-ink/70 text-white'
  } as const;
  return <span className={`text-[10px] font-bold tracking-[0.2em] uppercase px-3 py-1.5 rounded-full ${map[status]}`} data-id="element-1181">
      {status === 'Selling' && <span className="inline-block w-1.5 h-1.5 rounded-full bg-white mr-2 animate-pulse" data-id="element-1182" />}
      {status}
    </span>;
}
export function Land() {
  const [filter, setFilter] = useState<(typeof statusFilters)[number]>('All');
  const filtered = useMemo(() => filter === 'All' ? lands : lands.filter(l => l.status === filter), [filter]);
  const sellingCount = lands.filter(l => l.status === 'Selling').length;
  return <div className="pt-28 pb-24" data-id="element-1183">
      <PageHeader eyebrow="Properties" title="Land for Sale" subtitle={`Smart land investments across Lagos, Ogun, Abuja, Ibadan, and Enugu. ${sellingCount} estates currently selling.`} breadcrumb={[{
      label: 'Home',
      href: '/'
    }, {
      label: 'Properties'
    }, {
      label: 'Land'
    }]} data-id="element-1184" />

      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 md:px-12 mt-12" data-id="element-1185">
        <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2 hide-scrollbar" data-id="element-1186">
          <Filter className="w-4 h-4 text-ink/40 shrink-0" data-id="element-1187" />
          {statusFilters.map(s => {
          const count = s === 'All' ? lands.length : lands.filter(l => l.status === s).length;
          return <button key={s} onClick={() => setFilter(s)} className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-widest whitespace-nowrap transition-all flex items-center gap-2 ${filter === s ? 'bg-ink text-white shadow-md' : 'bg-white text-ink/70 border border-ink/10 hover:border-ink/30'}`} data-id="element-1188">
                {s}
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${filter === s ? 'bg-gold text-ink' : 'bg-ink/5 text-ink/50'}`} data-id="element-1189">
                  {count}
                </span>
              </button>;
        })}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1190">
          <AnimatePresence mode="popLayout" data-id="element-1191">
            {filtered.map((l, i) => <motion.div key={l.id} layout initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            scale: 0.9
          }} transition={{
            delay: i % 3 * 0.08,
            duration: 0.5
          }} whileHover={{
            y: -6
          }} data-id="element-1192">
                <Link to={`/land/${l.slug}`} className="group block bg-white rounded-2xl overflow-hidden border border-ink/5 shadow-sm hover:shadow-xl hover:border-gold/30 transition-all duration-500" data-id="element-1193">
                  <div className="relative aspect-[4/3] overflow-hidden bg-ink/5" data-id="element-1194">
                    <img src={l.hero} alt={l.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" data-id="element-1195" />
                    {l.status !== 'Selling' && <div className="absolute inset-0 bg-ink/30 backdrop-blur-[1px]" data-id="element-1196" />}
                    <div className="absolute top-3 left-3" data-id="element-1197">
                      <StatusPill status={l.status} data-id="element-1198" />
                    </div>
                    <div className="absolute top-3 right-3 bg-white/95 backdrop-blur-sm rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-ink flex items-center gap-1" data-id="element-1199">
                      <TreePine className="w-3 h-3" data-id="element-1200" /> Land
                    </div>
                  </div>

                  <div className="p-6" data-id="element-1201">
                    <h3 className="font-serif text-xl text-ink mb-2 group-hover:text-gold-dark transition-colors" data-id="element-1202">
                      {l.name}
                    </h3>
                    <div className="flex items-start gap-1.5 text-xs text-ink/60 mb-4" data-id="element-1203">
                      <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0 text-gold-dark" data-id="element-1204" />
                      <span data-id="element-1205">{l.location}</span>
                    </div>

                    {l.plotSizes && <div className="flex flex-wrap gap-1.5 mb-5 pb-5 border-b border-ink/5" data-id="element-1206">
                        {l.plotSizes.map(s => <span key={s} className="text-[10px] font-bold uppercase tracking-wider bg-bone text-ink/70 px-2.5 py-1 rounded-full inline-flex items-center gap-1" data-id="element-1207">
                            <Square className="w-3 h-3" data-id="element-1208" /> {s}
                          </span>)}
                      </div>}

                    <div className="flex items-center justify-between" data-id="element-1209">
                      <div data-id="element-1210">
                        <div className="text-[10px] font-bold uppercase tracking-widest text-ink/40" data-id="element-1211">
                          {l.status === 'Selling' ? 'From' : 'Status'}
                        </div>
                        <div className="font-serif text-lg text-ink" data-id="element-1212">
                          {l.priceFrom}
                        </div>
                      </div>
                      <div className="w-9 h-9 rounded-full bg-bone group-hover:bg-gold flex items-center justify-center transition-colors" data-id="element-1213">
                        <ArrowRight className="w-4 h-4 text-ink group-hover:translate-x-0.5 transition-transform" data-id="element-1214" />
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>)}
          </AnimatePresence>
        </div>
      </div>
    </div>;
}